import { DateTime } from 'luxon';
import { PouchDoc } from './pouch';

export class Exercise extends PouchDoc {
  // full name of the exercise
  name: string;
  // short name of the exercise (optional)
  shortname?: string;
  // long description of the exercise, in Markdown format
  description?: string;
  // percentage of bodyweight to add to weight
  bw_pc: number;
  // eventual strength standard
  standard?: any;
  // whether this exercise can use myo-reps
  allowMyoReps?: boolean;
  avoidRMTesting?: boolean;
  // recommended equipment template for this exercise - this specifes the id that is shared by both EU and US templates
  equipmentTemplateId?: string;
  // YouTube or Vimeo URL for demo video
  videoUrl?: string;
}

// The exercise fields the coach-update picker can diff and apply, in the order the picker
// lists them. This is the ONE list: `ExerciseDiffField`, `coachBaseFields` and the per-field
// table in client/src/services/exercise-diff.ts are all derived from it, so adding a field
// here makes the compiler ask for everything else it needs.
export const EXERCISE_DIFF_FIELDS = [
  'name', 'shortname', 'description', 'videoUrl', 'bw_pc',
  'allowMyoReps', 'avoidRMTesting', 'standard',
  'equipmentTemplateId', 'similarExerciseId',
] as const;

export type ExerciseDiffField = typeof EXERCISE_DIFF_FIELDS[number];

export class CustomExercise extends Exercise {
  usedInSlots: string[] = [];
  virtuallyDeleted?: boolean;
  // reference exercise ID for muscle targeting — the app uses this exercise's slot muscle factors for volume calculations
  similarExerciseId?: string;
  local?: {
    shared?: boolean;
    coachShared?: boolean;
    sourceExerciseId?: string;
    sourceRevision?: string;
    hashSource?: string;
    // The `global_exercises` staging `_rev` the coach-channel apply pipeline last
    // applied onto this userdb copy. Stamped by `stampCoachChannelRevision` in
    // `coach-exercise-sync.service.ts` inside `writeCoachDocIntoUserDbAsync`, the one
    // userdb-copy write shared by the first apply attempt and its 409 retry. The fork
    // path reads it as the lineage baseline so a fork's `sourceRevision` ends up as a
    // real staging-DB rev (same sequence as `checkImportedExerciseForUpdateAsync`'s
    // comparison target) rather than a userdb-local rev.
    coachChannelRevision?: string;
    // The staging-DB `_rev` the user last reviewed via the diff picker (R3 / Decision B).
    // Set by full apply, partial apply, and explicit ignore; NOT set by a cancel
    // (X / backdrop / hardware-back), which deliberately leaves the update unresolved. Read
    // by `computeHasUpdate` to stop the auto-banner / dot re-firing for an already-seen rev
    // (so a partial apply / ignore reads as resolved). NOT a lineage marker —
    // `local.sourceRevision` remains the rev the fork's content was actually derived from.
    lastSeenSourceRevision?: string;
    // 3-way-diff base: a snapshot of the SOURCE's diffable field values at the last
    // full-apply point (fork time, then advanced ONLY on a full apply — partial apply
    // and ignore deliberately leave it so a later "Check for Update" re-shows the
    // un-incorporated coach changes). The update picker offers a field only when the
    // source's CURRENT value differs from this base — i.e. the coach changed it since —
    // so the user's own fork-time divergences (e.g. a rename) are not re-offered as
    // "updates". Undefined on pre-existing forks (the diff falls back to a plain 2-way
    // fork-vs-source comparison until the next full apply populates it).
    coachBaseFields?: Partial<Pick<CustomExercise, ExerciseDiffField>>;
  };
  authorHash?: string;
  // Per-author SHA-256 stamped on publish to `global_exercises`; used by the coach-channel
  // filter to deliver only the user's coach's exercises. Public function of uid (no server
  // secret) — see `computeAuthorIdentityHash`. Coexists with `authorHash` (per-exercise HMAC
  // for ownership verification): different purposes, different formulas.
  authorIdentityHash?: string;
}

// Domain-separation prefix for `authorIdentityHash`. Imported by both the server (Node `crypto`)
// and client (Web Crypto) helpers — single source of truth for the formula. The `:v1` suffix
// reserves a future migration path: a `:v2` rotation can re-publish without breaking existing data.
export const AUTHOR_IDENTITY_HASH_DOMAIN = 'mh-physique:coach-author-identity:v1';

export type AnyExercise = Exercise | CustomExercise;

export function isCustomExercise(e: PouchDoc): e is CustomExercise {
  return e && e._id.startsWith('exercise/+UDE');
}

export function isCustomExerciseById(exerciseId: string): boolean {
  return exerciseId && exerciseId.startsWith('exercise/+UDE');
}

/** True when the id names a CURATED exercise — one of ours, from the read-only
 *  `global_common` catalog, as opposed to one the user made themselves.
 *
 *  This is the other half of `isCustomExerciseById`, and it exists so the
 *  `exercise/$` literal is spelled in one place. Several call sites ask this
 *  question before deciding which database to read; see
 *  `client/src/services/exercise-resolve.ts` for what the answer means. */
export function isCuratedExerciseById(exerciseId: string): boolean {
  return !!exerciseId && exerciseId.startsWith('exercise/$');
}

/** True when the UDE is a FORK (carries upstream lineage) — i.e. its `local.sourceExerciseId`
 * is set AND points to a different exercise than itself. The `src === _id` exclusion is
 * the import-self-reference: today's import flow without lineage data writes
 * `imported.local.sourceExerciseId = imported._id`, so an imported UDE with no upstream
 * lineage has `sourceExerciseId === _id` and is NOT a fork.
 *
 * Tolerates a null/undefined `ude` (returns false) so call sites don't need their own
 * guard before the page's exercise has loaded.
 *
 * Used by the picker hide-source filter, the picker's soft-delete-only override for forks,
 * and the edit page's Revert visibility gate. */
export function isForkUde(ude: CustomExercise | null | undefined): boolean {
  const src = ude?.local?.sourceExerciseId;
  return src !== undefined && src !== ude._id;
}

/** True when a custom exercise (`+UDE-`) belongs to the current user, so editing it must
 * save IN PLACE rather than forking into a new copy.
 *
 * Two ways a UDE counts as owned:
 *   1. Its `local.hashSource` prefix matches the current uid — the normal case, set by
 *      `generateCustomExerciseId` whenever this user creates or forks an exercise.
 *   2. It has NO `hashSource` AND carries no "this came from someone else" marker
 *      (`sourceExerciseId` / `authorIdentityHash` / `coachShared` / `shared`). This is a
 *      LEGACY own doc: the user created it before the `hashSource` stamp existed, so it has
 *      no stamp and no lineage. A genuine foreign exercise always carries one of those
 *      markers, so their absence means the doc is the user's own.
 *
 * Without case 2, editing such a legacy own exercise was wrongly treated as editing a
 * foreign exercise and forked a duplicate (issue #3808). Globals (no `+UDE-` id) and any
 * UDE that still carries a foreign marker are NOT owned and still fork on edit — unchanged.
 *
 * Tolerates a null/undefined `ex` (returns false) so call sites don't need their own guard. */
export function isOwnedCustomExercise(ex: AnyExercise | null | undefined, uid: string | undefined): boolean {
  if (!ex || !isCustomExerciseById(ex._id)) return false;
  const ce = ex as CustomExercise;
  const hashSource = ce.local?.hashSource;
  if (hashSource) {
    // case 1: a stamped doc is owned only when the stamp's uid prefix matches the current user
    return !!uid && hashSource.split(':')[0] === uid;
  }
  // case 2: a bare legacy doc is owned when it has no foreign-origin marker
  return !ce.local?.sourceExerciseId
    && !ce.authorIdentityHash
    && !ce.local?.coachShared
    && !ce.local?.shared;
}

export const exerciseDatabaseName = 'global_exercises';
export const exerciseAuthorHashSecret = 'kR7vQm2xWp9TfN3jYcBe';
export const exerciseHashSecret = 'Xt8wLm4pHn6KdF2sUvAq';

// Per-user singleton tracking which `global_exercises` doc IDs the user has imported via
// the public-share flow. The list drives the `byImportedIds` filtered replication into the
// client-side staging DB. Loaded as one of the user database's boot singletons so the
// DocWatcher in `coach-exercise-sync.service` can fire on first access without missing
// updates. URL-length cap on the replication query: see Phase 2 PRD §8 (close-out commit 31db784b0).
export interface ImportedGlobalExercisesIds extends PouchDoc {
  ids: string[];
}
export const importedGlobalExercisesIdsId = 'meta/imported-global-exercise-ids';

/** Returns true if the exercise ID uses the old name-based format (e.g. exercise/+UDE-my_bench_press)
 * rather than the new SHA-based hex format. */
export function isNameBasedExerciseId(id: string): boolean {
  const match = id.match(/^exercise\/\+UDE-(.+)$/);
  if (!match) return false;
  // SHA-based IDs are hex-only (0-9a-f), name-based IDs contain other characters
  return !/^[0-9a-f]+$/.test(match[1]);
}

/** Generate a SHA-based exercise ID, following the same pattern as generateUserFoodId(). */
export function generateCustomExerciseId(userId: string, exercise: CustomExercise, crypto: any) {
  let time = DateTime.utc().toISO();

  exercise.local = exercise.local || {};
  exercise.local.hashSource = userId + ':' + time;
  exercise._id = customExerciseIdForHashSource(exercise.local.hashSource, crypto);
}

/** The one rule for a custom exercise's id: `exercise/+UDE-` followed by the hex HMAC-SHA1 of
 * its `local.hashSource` under `exerciseHashSecret`. The app mints ids with it, and the server
 * recomputes it to refuse an id that does not match. `crypto` is injected because this file is
 * bundled into the client, which cannot import node's `crypto`. */
export function customExerciseIdForHashSource(hashSource: string, crypto: any): string {
  const hash = crypto.createHmac('SHA1', exerciseHashSecret).update(hashSource).digest('hex');
  return `exercise/+UDE-${hash}`;
}
